import React, { useState } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { ShowtimeDTO } from '../models/showtime';
import Button from './atoms/Button';
import { formatDateForTable } from '../utils/dates';
import { useDeleteShowtimeMutation } from '../store';
import CustomModal from './atoms/CustomModal';
import CreateEditShowtime from './CreateEditShowtime';
import MenuDropdown from './atoms/MenuDropdown';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';

interface Props {
  showtimes: ShowtimeDTO[];
  eventId: string;
  businessId: string;
  button?: {
    label: string;
    image?: string;
    onClick: () => void;
    variant: 'primary' | 'secondary' | 'tertiary';
  };
}

interface TableShowtimes extends BaseData {
  id: string;
  starts_at: string;
  ends_at: string;
  active: JSX.Element;
}

const ShowtimesTable: React.FC<Props> = ({
  showtimes, eventId, businessId, button,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit'>('delete');
  const [selectedShowtime, setSelectedShowtime] = useState<ShowtimeDTO | null>(null);
  const [deleteShowtime, { isLoading: isDeleting }] = useDeleteShowtimeMutation();

  const handleConfirmDeleteShowtime = (showtime: ShowtimeDTO | null) => {
    if (!showtime) {
      return;
    }
    deleteShowtime({
      businessId,
      eventId,
      showtimeId: showtime.id,
    });
    setShowModal(false);
  };

  const handleClose = () => setShowModal(false);

  const handleDeleteShowtime = (showtime: ShowtimeDTO) => {
    setSelectedShowtime(showtime);
    setModalType('delete');
    setShowModal(true);
  };

  const handleEditShowtime = (showtime: ShowtimeDTO) => {
    setSelectedShowtime(showtime);
    setModalType('edit');
    setShowModal(true);
  };

  const dropDownOptions = [
    {
      label: 'Editar',
      onClick: handleEditShowtime,
    },
    {
      label: 'Eliminar',
      onClick: handleDeleteShowtime,
    },
  ];

  const tableConfig = [
    { label: 'Activa', id: 'active' },
    { label: 'Inicio', id: 'starts_at' },
    { label: 'Fin', id: 'ends_at' },
  ];

  const actions = (row: TableShowtimes) => {
    const showtime = showtimes.find((s) => s.id === row.id);
    if (!showtime) {
      throw new Error('Showtime not found');
    }
    return (
      <MenuDropdown<ShowtimeDTO>
        options={dropDownOptions}
        data={showtime}
        loading={isDeleting}
      />
    );
  };

  const customButton = button && <Button onClick={button.onClick} variant={button.variant}>
    <div className='flex flex-row gap-2 items-center'>
      <img src={button.image} alt={button.label} className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>{button.label}</p>
    </div>
  </Button>;

  const rows: TableShowtimes[] = showtimes.slice().sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()).map((showtime) => ({
    id: showtime.id,
    starts_at: formatDateForTable(showtime.starts_at),
    ends_at: formatDateForTable(showtime.ends_at),
    active: showtime.active ? (
      <div className='tag-primary'>
        Habilitada
      </div>
    ) : (
      <div className='tag-warning'>
        No habilitada
      </div>
    ),
  }));

  return (
    <div className="glass-surface">
      <h1 className="text-2xl font-medium mb-2">Funciones</h1>
      {showtimes.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={tableConfig}
          pagination={{
            enabled: true,
            pageSize: 10,
          }}
          searchEnabled
          actions={actions}
          customButtons={customButton ? [customButton] : []}
          tableId="showtimes-table"
        />
      ) : (
        <EmptyTable
          title="Aún no hay funciones en este evento"
          info="Aquí aparecerán todas las funciones que crees en este evento"
          icon={AiOutlineClockCircle}
          iconColor="text-secondary-500"
          button={button ? [{ ...button }] : undefined}
        />
      )}
      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton
      >
        {modalType === 'delete' && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-medium">Eliminar función</h1>
              <p>
                ¿Deseas eliminar esta función?
              </p>
              <p className="mt-2">Una vez eliminada desaparecerá de la lista y no se podrá revertir.</p>
            </div>
            <div className="flex flex-row gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => handleConfirmDeleteShowtime(selectedShowtime)}>
                Eliminar
              </Button>
            </div>
          </div>
        )}
        {modalType === 'edit' && (
          <div className="flex flex-col">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-medium">Editar función</h1>
              <p className="text-text-tertiary text-sm">
                Podrás realizar las ediciones que necesites en esta función
              </p>
            </div>
            <CreateEditShowtime showtime={selectedShowtime || undefined} onSuccess={handleClose} onCancel={() => setShowModal(false)} />
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default ShowtimesTable;
